import React from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { IBoard } from '../../flux/boards/BoardStore'

/**
 * Properties passed to the delete board modal component.
 */
export interface IDeleteBoardModalProps {
    board: IBoard
    onConfirm: () => void
    onCancel: () => void
}

function DeleteBoardModalComponent(props: IDeleteBoardModalProps) {
    const taskCount = props.board.swimlanes.flatMap((x) => x.tasks).length
    const hasTasks = taskCount !== 0
    const taskLabel = !hasTasks ? '' : taskCount > 1 ? 'tasks' : 'task'
    const swimlaneLabels = {
        general: { label: '', count: props.board.swimlanes.length },
        empty: {
            label: '',
            count: props.board.swimlanes.filter(
                (lane) => lane.tasks.length === 0
            ).length,
        },
        populated: {
            label: '',
            count: props.board.swimlanes.filter(
                (lane) => lane.tasks.length !== 0
            ).length,
        },
    }
    swimlaneLabels.general.label =
        swimlaneLabels.general.count > 1 ? 'swimlanes' : 'swimlane'
    swimlaneLabels.empty.label =
        swimlaneLabels.empty.count > 1 ? 'swimlanes' : 'swimlane'
    swimlaneLabels.populated.label =
        swimlaneLabels.populated.count > 1 ? 'swimlanes' : 'swimlane'

    /**
     * Returns the heading as an appropriatley formatted JSX element
     * based upon the parameters supplied
     */
    function getHeading(): JSX.Element {
        if (!hasTasks)
            return (
                <span>Delete Board with {swimlaneLabels.general.label}?</span>
            )
        return (
            <span>
                Delete Board with {swimlaneLabels.general.label} and {taskLabel}
                ?
            </span>
        )
    }

    /**
     * Returns the body as an appropriatley formatted JSX element
     * based upon the parameters supplied
     */
    function getBody(): JSX.Element {
        if (!hasTasks)
            // no tasks! output simple warning about swimlanes
            return (
                <p>
                    By deleting this board you will also delete{' '}
                    <span>
                        {swimlaneLabels.general.count} empty{' '}
                        {swimlaneLabels.general.label}
                    </span>
                    , are you sure?
                </p>
            )

        // has tasks, calculate error message for displaying how many
        // empty and populated swimlanes and how many tasks.
        const emptySwimlanes =
            swimlaneLabels.empty.count > 0 ? (
                <li>
                    {swimlaneLabels.empty.count} empty{' '}
                    {swimlaneLabels.empty.label}
                </li>
            ) : (
                <></>
            )
        return (
            <>
                <p>By deleting this board you will also delete:</p>
                <ul>
                    {emptySwimlanes}
                    <li>
                        {swimlaneLabels.populated.count} populated{' '}
                        {swimlaneLabels.populated.label}
                    </li>
                    <li>
                        {taskCount} {taskLabel}
                    </li>
                </ul>
                <p>Are you sure?</p>
            </>
        )
    }

    return (
        <Modal show={true}>
            <Modal.Header>
                <Modal.Title>{getHeading()}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{getBody()}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onCancel}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={props.onConfirm}>
                    Confirm
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default DeleteBoardModalComponent
